'use client';

import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { Box, Stack, TextField, CircularProgress, MenuItem } from '@mui/material';
import { Formik } from 'formik';
import * as Yup from 'yup';

import PickerField from '../../../components/PickerField';
import usePickerPagination from '../../../hooks/usePickerPagination';
import { createServiceType, updateServiceType, getServiceTypeById } from '../../../stores/serviceTypeStore';
import { useCompanyDataList } from '../../../stores/companyStore';
import { useLanguage } from '../../../utils/i18n';

const ServiceTypeAddEdit = forwardRef(
  ({ id = null, onClose = null, onSuccess = null, onError = null, onSubmitStart = () => {}, onSubmitEnd = () => {} }, ref) => {
    const { t } = useLanguage();
    const [initialValues, setInitialValues] = useState({
      company: '',
      company_name: '',
      name: '',
      service_cost: 0,
      service_length_unit: 0,
      interval_time: 0,
      tracehold_time_warning: 0,
      tracehold: 0,
      interval_distance: 0,
      tracehold_distance_warning: 0,
      tracehold_distance: 0
    });

    const [loading, setLoading] = useState(Boolean(id));
    const formikRef = useRef();

    useImperativeHandle(ref, () => ({
      submitForm: () => formikRef.current?.submitForm?.(),
      getValues: () => formikRef.current?.values
    }));
    useEffect(() => {
      let mounted = true;
      const fetch = async () => {
        if (!id) {
          setLoading(false);
          return;
        }
        setLoading(true);
        try {
          const data = await getServiceTypeById(id);
          if (!mounted) return;
          if (data) {
            setInitialValues({
              company: data.company != null ? String(data.company) : '',
              company_name: data.company_name ?? data.join?.company_name ?? '',
              name: data.name ?? '',
              service_cost: data.service_cost ?? 0,
              service_length_unit: data.service_length_unit ?? '',
              interval_time: data.interval_time ?? '',
              tracehold_time_warning: data.tracehold_time_warning ?? '',
              tracehold: data.tracehold ?? '',
              interval_distance: data.interval_distance ?? '',
              tracehold_distance_warning: data.tracehold_distance_warning ?? '',
              tracehold_distance: data.tracehold_distance ?? ''
            });
          }
        } catch (err) {
          console.error('Failed to load Service Type:', err);
          onError && onError(t('serviceType.failedLoadData', 'Failed to load data'));
        } finally {
          if (mounted) setLoading(false);
        }
      };
      fetch();
      return () => {
        mounted = false;
      };
    }, [id, onError, t]);
    useEffect(() => {
      if (id) return;
      const stored = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null;
      if (!stored) return;

      const firstId = stored.split(',')[0]?.trim();
      if (firstId) {
        setInitialValues((prev) => ({
          ...prev,
          company: Number(firstId) || firstId
        }));
      }
    }, [id]);

    const validationSchema = Yup.object().shape({
      company: Yup.mixed().required(t('serviceType.validationCompany', 'Company is required')),
      name: Yup.string().required(t('serviceType.validationName', 'Name is required')),
      service_length_unit: Yup.string().required(t('serviceType.validationScheduleBy', 'Schedule By is required')),
      // Validasi Distance Threshold
      tracehold_distance: Yup.number().test(
        'danger-less-than-warning-dist',
        t('serviceType.validationDangerSmaller', 'Danger threshold must be smaller than warning threshold'),
        function (value) {
          const { tracehold_distance_warning, service_length_unit } = this.parent;
          if (service_length_unit === 'distance' || service_length_unit === 'both') {
            return value < tracehold_distance_warning;
          }
          return true;
        }
      ),
      tracehold: Yup.number().test(
        'danger-less-than-warning-time',
        t('serviceType.validationDangerSmaller', 'Danger threshold must be smaller than warning threshold'),
        function (value) {
          const { tracehold_time_warning, service_length_unit } = this.parent;
          const needsValidation = ['time', 'both', 'engine_work_time'].includes(service_length_unit);
          if (needsValidation) {
            return value < tracehold_time_warning;
          }
          return true;
        }
      ),
      interval_time: Yup.number().test(
        'interval-higher-than-warning',
        t('serviceType.validationRescheduleHigher', 'Reschedule Every must be higher than Threshold Warning'),
        function (value) {
          const { tracehold_time_warning, service_length_unit } = this.parent;
          const needsValidation = ['time', 'both', 'engine_work_time'].includes(service_length_unit);
          if (needsValidation) {
            return value > tracehold_time_warning;
          }
          return true;
        }
      )
    });

    const handleSubmit = async (values, { setSubmitting }) => {
      setSubmitting(true);
      onSubmitStart();
      try {
        const payload = {
          ...values,
          id: id ?? 0,
          company: Number(values.company),
          service_cost: Number(values.service_cost)
        };

        if (id) {
          await updateServiceType(id, payload);
          onSuccess && onSuccess(t('serviceType.updatedSuccess', 'Updated successfully'));
        } else {
          await createServiceType(payload);
          onSuccess && onSuccess(t('serviceType.createdSuccess', 'Created successfully'));
        }
        onClose && onClose();
      } catch (err) {
        const errorMessage = err?.response?.data?.message || err?.message || 'Failed to save data';
        onError && onError(errorMessage ?? t('serviceType.submitFailed', 'Submit failed'));
      } finally {
        setSubmitting(false);
        onSubmitEnd();
      }
    };

    const companyPickerParams = usePickerPagination({
      fetchHook: useCompanyDataList,
      buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue],
      searchKey: 'name',
      searchPlaceholder: t('general.search', 'Search...')
    });

    if (loading)
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      );

    return (
      <Box sx={{ width: '100%' }}>
        <Formik
          innerRef={formikRef}
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, handleChange, handleBlur, setFieldValue, touched, errors }) => {
            const isTimeEnabled = values.service_length_unit === 'time' || values.service_length_unit === 'both';
            const isDistanceEnabled = values.service_length_unit === 'distance' || values.service_length_unit === 'both';
            const isEngineEnabled = values.service_length_unit === 'engine_work_time';
            const handleScheduleChange = (e) => {
              const newVal = e.target.value;
              setFieldValue('service_length_unit', newVal);

              // Jika bukan mode Edit (id null), lakukan reset nilai
              if (!id) {
                if (newVal === 'distance') {
                  setFieldValue('interval_time', 0);
                  setFieldValue('tracehold_time_warning', 0);
                  setFieldValue('tracehold', 0);
                } else if (newVal === 'time' || newVal === 'engine_work_time') {
                  setFieldValue('interval_distance', 0);
                  setFieldValue('tracehold_distance_warning', 0);
                  setFieldValue('tracehold_distance', 0);
                }
              }
            };
            return (
              <form onSubmit={handleSubmit}>
                <Box sx={{ width: '100%', maxWidth: 'none' }}>
                  <Box sx={{ display: 'flex', gap: 4, alignItems: 'stretch', width: '100%' }}>
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <PickerField
                        label={t('serviceType.company', 'Company *')}
                        {...companyPickerParams}
                        value={values.company || ''}
                        onChange={(val) => {
                          setFieldValue('company', val || '');
                          const opt =
                            companyPickerParams.options.find((o) => String(o.value) === String(val)) ||
                            companyPickerParams.listOptions?.find((o) => String(o.value) === String(val));
                          setFieldValue('company_name', opt?.raw?.name ?? opt?.label ?? '');
                        }}
                        error={touched.company && Boolean(errors.company)}
                        helperText={touched.company && errors.company}
                      />
                      <TextField
                        select
                        label={t('serviceType.scheduleByRequired', 'Schedule By *')}
                        name="service_length_unit"
                        value={values.service_length_unit}
                        onChange={handleScheduleChange}
                        disabled={Boolean(id)}
                        fullWidth
                      >
                        <MenuItem value="time">{t('serviceType.time', 'Time')}</MenuItem>
                        <MenuItem value="distance">{t('serviceType.distance', 'Distance')}</MenuItem>
                        <MenuItem value="engine_work_time">{t('serviceType.engineWorkTime', 'Engine Work Time')}</MenuItem>
                        <MenuItem value="both">{t('serviceType.timeAndDistance', 'Time and Distance')}</MenuItem>
                      </TextField>
                      <TextField
                        label={t('serviceType.rescheduleEveryKm', 'Reschedule Every (KM)')}
                        name="interval_distance"
                        type="number"
                        value={values.interval_distance}
                        onChange={handleChange}
                        disabled={!isDistanceEnabled}
                        fullWidth
                      />
                      <TextField
                        label={
                          isEngineEnabled
                            ? t('serviceType.rescheduleEveryEngine', 'Reschedule Every (Engine Work Time)')
                            : t('serviceType.rescheduleEveryDays', 'Reschedule Every (Days)')
                        }
                        name="interval_time"
                        type="number"
                        value={values.interval_time}
                        onChange={handleChange}
                        disabled={!isTimeEnabled && !isEngineEnabled}
                        fullWidth
                        error={touched.interval_time && Boolean(errors.interval_time)}
                        helperText={touched.interval_time && errors.interval_time}
                      />
                    </Box>
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <TextField
                        label={t('serviceType.serviceTypeName', 'Service Type Name *')}
                        name="name"
                        value={values.name}
                        onChange={handleChange}
                        fullWidth
                        error={touched.name && Boolean(errors.name)}
                        helperText={touched.name && errors.name}
                      />

                      <TextField
                        label={t('serviceType.serviceCost', 'Service Cost')}
                        name="service_cost"
                        type="text"
                        value={
                          values.service_cost != null
                            ? new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(
                                values.service_cost
                              )
                            : ''
                        }
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9]/g, '');
                          setFieldValue('service_cost', val ? Number(val) : 0);
                        }}
                        fullWidth
                      />
                      {/* SECTION DISTANCE (KM) */}
                      <Stack direction="row" spacing={2}>
                        <TextField
                          label={t('serviceType.thresholdWarningDistance', 'Threshold Warning By Distance')}
                          name="tracehold_distance_warning"
                          type="number"
                          value={values.tracehold_distance_warning}
                          onChange={handleChange}
                          disabled={!isDistanceEnabled}
                          fullWidth
                        />
                        <TextField
                          label={t('serviceType.thresholdDangerDistance', 'Threshold Danger By Distance')}
                          name="tracehold_distance"
                          type="number"
                          value={values.tracehold_distance}
                          onChange={handleChange}
                          disabled={!isDistanceEnabled}
                          fullWidth
                        />
                      </Stack>
                      <Stack direction="row" spacing={2}>
                        <TextField
                          label={
                            isEngineEnabled
                              ? t('serviceType.thresholdWarningEngine', 'Threshold Warning By Engine Work Time')
                              : t('serviceType.thresholdWarningTime', 'Threshold Warning By Time')
                          }
                          name="tracehold_time_warning"
                          type="number"
                          value={values.tracehold_time_warning}
                          onChange={handleChange}
                          disabled={!isTimeEnabled && !isEngineEnabled}
                          fullWidth
                        />
                        <TextField
                          label={
                            isEngineEnabled
                              ? t('serviceType.thresholdDangerEngine', 'Threshold Danger By Engine Work Time')
                              : t('serviceType.thresholdDangerTime', 'Threshold Danger By Time')
                          }
                          name="tracehold"
                          type="number"
                          value={values.tracehold}
                          onChange={handleChange}
                          disabled={!isTimeEnabled && !isEngineEnabled}
                          fullWidth
                        />
                      </Stack>
                    </Box>
                  </Box>
                </Box>
              </form>
            );
          }}
        </Formik>
      </Box>
    );
  }
);

export default ServiceTypeAddEdit;
